import { useTranslation } from 'react-i18next';

import { DEFAULT_LOCALE, isLocale, type Locale } from '../app/i18n';

/**
 * Locale-aware path helpers (§5, §11).
 *
 * These live apart from the components that use them so that component modules export
 * only components — the condition React Fast Refresh needs to hot-reload reliably.
 */

/**
 * Builds a locale-correct href. English is unprefixed, Georgian is `/ka/...`.
 * Every internal link must go through this — hand-written paths are how half a site
 * ends up silently switching back to English mid-session.
 */
export function localizedPath(path: string, locale: Locale): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (locale === DEFAULT_LOCALE) return normalized;
  return normalized === '/' ? `/${locale}` : `/${locale}${normalized}`;
}

/** The current path with any locale prefix stripped — used by the language switcher. */
export function pathWithoutLocale(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  if (isLocale(segments[0])) segments.shift();
  return `/${segments.join('/')}`;
}

/** The locale currently active in the UI. */
export function useCurrentLocale(): Locale {
  const { i18n } = useTranslation();
  return isLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE;
}
